import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  TablePagination,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { RenewalPolicy } from '@/types/renewalDashboard';
import { PolicyLinkMetrics } from '@/components/sms/PolicyLinkMetrics';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface RenewalPoliciesTableProps {
  policies: RenewalPolicy[];
  loading?: boolean;
  error?: string | null;
  pagination: {
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRefresh?: () => void;
  onExport?: () => void;
}

const RenewalPoliciesTable: React.FC<RenewalPoliciesTableProps> = ({
  policies,
  loading = false,
  error = null,
  pagination,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  onExport
}) => {
  const [linkMetricsPolicy, setLinkMetricsPolicy] = useState<string | null>(null);

  const getSmsStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      case 'queued':
        return 'info';
      default:
        return 'default';
    }
  };

  const getExpiryStatusColor = (daysToExpiry: number): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (daysToExpiry < 0) return 'error'; // Expired
    if (daysToExpiry <= 1) return 'error'; // Expires today or tomorrow
    if (daysToExpiry <= 7) return 'warning'; // Expires within a week
    if (daysToExpiry <= 30) return 'info'; // Expires within a month
    return 'success'; // Expires later
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatMobile = (mobile: string): string => {
    // Format mobile number for display
    if (mobile.length === 10) {
      return `${mobile.slice(0, 5)} ${mobile.slice(5)}`;
    }
    return mobile;
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading policies...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {formatErrorForDisplay(error)}
      </Alert>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Table Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Typography variant="h6">
          Renewal Policies ({pagination.total.toLocaleString()})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onRefresh && (
            <Tooltip title="Refresh">
              <IconButton onClick={onRefresh} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          {onExport && (
            <Tooltip title="Export">
              <IconButton onClick={onExport} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Policy Number</TableCell>
              <TableCell>Month</TableCell>
              <TableCell>Branch Office</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Days to Expiry</TableCell>
              <TableCell>Customer Mobile</TableCell>
              <TableCell>SMS Status</TableCell>
              <TableCell>SMS Sent At</TableCell>
              <TableCell>Product Name</TableCell>
              <TableCell>Agent Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {policies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No policies found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              policies.map((policy, index) => (
                <TableRow key={`${policy.policy_number}-${index}`} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {policy.policy_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={policy.month}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {policy.branch_office}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(policy.red)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${policy.days_to_expiry} days`}
                      size="small"
                      color={getExpiryStatusColor(policy.days_to_expiry)}
                      variant={policy.days_to_expiry <= 7 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {formatMobile(policy.customer_mobile)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={policy.sms_status}
                      size="small"
                      color={getSmsStatusColor(policy.sms_status)}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    {policy.sms_sent_at ? (
                      <Typography variant="body2">
                        {formatDate(policy.sms_sent_at)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not sent
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {policy.product_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {policy.agent_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View SMS Link Activity">
                      <IconButton
                        size="small"
                        onClick={() => setLinkMetricsPolicy(policy.policy_number)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={pagination.total}
        rowsPerPage={pagination.page_size}
        page={pagination.page - 1} // MUI uses 0-based indexing
        onPageChange={(_, newPage) => onPageChange(newPage + 1)} // Convert back to 1-based
        onRowsPerPageChange={(event) => onPageSizeChange(parseInt(event.target.value, 10))}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
      />
      
      {/* Link Metrics Dialog */}
      <Dialog
        open={!!linkMetricsPolicy}
        onClose={() => setLinkMetricsPolicy(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          SMS Link Activity{linkMetricsPolicy ? ` – ${linkMetricsPolicy}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {linkMetricsPolicy && (
            <PolicyLinkMetrics policyNumber={linkMetricsPolicy} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkMetricsPolicy(null)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RenewalPoliciesTable;












